import { THEME } from '@common/constants/theme.constant';
import { ILinkItem } from '@common/utils/make-links/make-links.util';
import { SerializedStyles } from '@emotion/react';
import React, { ReactNode, useCallback } from 'react';
import { Link as ReactLink } from 'react-router-dom';
import { css } from 'twin.macro';

interface IProps {
    name: string;
    href?: ILinkItem;
    children?: ReactNode;
    className?: string;
    onClick?: (name: string) => void;
    target?: string;
    state?: unknown;
    noStyle?: boolean;
}

// eslint-disable-next-line react/display-name
export const Link = React.forwardRef<HTMLSpanElement, IProps>((props, _ref): JSX.Element => {
    const handleClick = useCallback(() => {
        props.onClick && props.onClick(props.name);
    }, [props]);

    const renderLink = (): JSX.Element => {
        const external = props.href?.toString().includes('http') || props.href?.toString().includes('mailto:');
        if (external) {
            return (
                <a
                    onClick={handleClick}
                    css={props.noStyle ? undefined : styles.link(!!props.href)}
                    className={props.className}
                    href={props.href?.toString()}
                    data-testid={`link-${props.name}`}
                    rel="noreferrer"
                >
                    {props.children}
                </a>
            );
        }
        const href = props.href?.toString() || '';
        const idx = href.indexOf('?');
        const search = idx > 0 ? href.substring(idx + 1) : undefined;
        return (
            <ReactLink
                target={props.target}
                onClick={handleClick}
                css={props.noStyle ? undefined : styles.link(!!props.href)}
                className={props.className}
                to={{
                    pathname: href.substring(0, idx !== -1 ? idx : undefined),
                    state: props.state,
                    search,
                }}
                data-testid={`link-${props.name}`}
            >
                {props.children as unknown as JSX.Element}
            </ReactLink>
        );
    };
    return props.href ? (
        renderLink()
    ) : (
        <a
            className={props.className}
            onClick={handleClick}
            css={props.noStyle ? undefined : styles.link(!!props.href)}
            data-testid={`link-${props.name}`}
        >
            {props.children}
        </a>
    );
});

const styles = {
    link: (hasLink: boolean): (SerializedStyles | boolean)[] => [
        css`
            cursor: default;
            color: ${THEME.GREEN};
            font-size: ${THEME.BASE_FONT_SIZE};
            text-underline-offset: 6px;
        `,
        hasLink &&
        css`
                cursor: pointer;
                :hover {
                    text-decoration: underline;
                }

                :focus {
                    text-decoration: underline;
                }
            `,
    ],
};

interface INavigationLinkProps extends IProps {
    icon?: ReactNode;
}
export const NavigationLink = ({ icon, ...rest }: INavigationLinkProps): JSX.Element => {
    return (
        <Link tw="underline text-green" {...rest}>
            {icon}
            {rest.children}
        </Link>
    );
};
